import { Element } from '@thekwallace/backtime-shared';

/**
 * Pure helpers for single-level visual grouping of timeline elements.
 *
 * Model (shipped): flat Element[] with `parentId` (null = top-level) and `sortOrder`
 * scoped to siblings (elements that share a parentId). A "group" is an element that has
 * children. Groups are visual-only — timing utilities ignore parentId and operate on leaves.
 *
 * MVP constraint: single level only (a group cannot contain another group).
 */

export type DropPosition = 'before' | 'after' | 'into';

/** True if the element currently has any children. */
export function hasChildren(elements: Element[], id: string): boolean {
  return elements.some((e) => e.parentId === id);
}

/**
 * Canonical invariant: a group is EXACTLY an element with `duration === null` (a visual-only
 * container whose duration is calculated from children). We deliberately do NOT treat "has
 * children" as group-ness — `parentId` is overloaded in historical data (iOS attached floating
 * children to a positive-duration anchor), so keying off children would misclassify the anchor.
 */
export function isGroup(_elements: Element[], el: Element): boolean {
  return el.duration === null;
}

/** Top-level elements (parentId == null) sorted by sortOrder. */
export function topLevelElements(elements: Element[]): Element[] {
  return elements.filter((e) => e.parentId == null).sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Leaves in true tree/display order: top-level elements by sortOrder, with each group's
 * children expanded in place; group (parent) elements removed. Use this for timing —
 * raw global sortOrder is sibling-scoped after grouping and would mis-order the sequence.
 */
export function flattenLeafElements(elements: Element[]): Element[] {
  const out: Element[] = [];
  for (const el of topLevelElements(elements)) {
    const kids = childrenOf(elements, el.id);
    if (el.duration === null) {
      // Group: replace with its leaf children.
      for (const k of kids) if (k.duration !== null) out.push(k);
    } else {
      // Leaf: emit it, then any (defensive) mis-parented leaf children so nothing is lost.
      out.push(el);
      for (const k of kids) if (k.duration !== null) out.push(k);
    }
  }
  // Re-index sortOrder to the flattened sequence so timing utilities (which sort by
  // sortOrder) keep this tree order instead of the sibling-scoped values.
  return out.map((el, i) => ({ ...el, sortOrder: i }));
}

/** Children of a given parent, sorted by sortOrder. */
export function childrenOf(elements: Element[], parentId: string): Element[] {
  return elements.filter((e) => e.parentId === parentId).sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Reassign sortOrder 0..n within every sibling scope, preserving current order. */
function normalizeSiblings(elements: Element[]): Element[] {
  const scopes = new Map<string, Element[]>();
  for (const el of elements) {
    const key = el.parentId ?? '__root__';
    if (!scopes.has(key)) scopes.set(key, []);
    scopes.get(key)!.push(el);
  }
  const sortOrderById = new Map<string, number>();
  for (const list of scopes.values()) {
    list.sort((a, b) => a.sortOrder - b.sortOrder).forEach((el, idx) => sortOrderById.set(el.id, idx));
  }
  return elements.map((el) => ({ ...el, sortOrder: sortOrderById.get(el.id) ?? el.sortOrder }));
}

/**
 * Move `draggedId` relative to `targetId`.
 * - 'before' | 'after': becomes a sibling of target (same parentId), placed accordingly.
 * - 'into': join target as a child. If target is a leaf, wrap target + dragged in a new group
 *   (requires opts.newGroupId + opts.groupName).
 *
 * Single-level guards: a group cannot be moved 'into' anything; 'into' a child is treated as 'after'.
 * Returns the new elements array (sibling-normalized) and the created group id (if any).
 */
export function moveElement(
  elements: Element[],
  draggedId: string,
  targetId: string,
  position: DropPosition,
  opts?: { newGroupId?: string; groupName?: string; now?: string }
): { elements: Element[]; createdGroupId: string | null } {
  if (draggedId === targetId) return { elements, createdGroupId: null };
  const dragged = elements.find((e) => e.id === draggedId);
  const target = elements.find((e) => e.id === targetId);
  if (!dragged || !target) return { elements, createdGroupId: null };

  const draggedIsGroup = isGroup(elements, dragged);
  // Don't allow dropping a group into another element (single level), and don't nest into a child.
  let effectivePosition = position;
  if (position === 'into' && (draggedIsGroup || target.parentId != null)) {
    effectivePosition = 'after';
  }

  let next = elements.map((e) => ({ ...e }));
  let createdGroupId: string | null = null;
  const now = opts?.now ?? new Date().toISOString();
  const d = next.find((e) => e.id === draggedId)!;
  const t = next.find((e) => e.id === targetId)!;

  if (effectivePosition === 'into') {
    if (isGroup(next, t)) {
      // Join an existing group: append to its children.
      const maxOrder = Math.max(-1, ...next.filter((e) => e.parentId === t.id).map((e) => e.sortOrder));
      d.parentId = t.id;
      d.sortOrder = maxOrder + 1;
      d.updatedAt = now;
    } else {
      // Wrap target + dragged in a new group at target's position.
      const groupId = opts?.newGroupId ?? `grp-${targetId}`;
      createdGroupId = groupId;
      const group: Element = {
        id: groupId,
        timelineId: t.timelineId,
        parentId: t.parentId, // top-level (target is top-level here)
        description: opts?.groupName || 'Group',
        duration: null, // calculated from children
        anchorOffset: null,
        sortOrder: t.sortOrder, // take target's slot
        createdAt: now,
        updatedAt: now,
      };
      t.parentId = groupId; t.sortOrder = 0; t.updatedAt = now;
      d.parentId = groupId; d.sortOrder = 1; d.updatedAt = now;
      next.push(group);
    }
  } else {
    // Sibling placement relative to target.
    d.parentId = t.parentId;
    // Place dragged just before/after target by nudging its sortOrder; normalize fixes the rest.
    d.sortOrder = effectivePosition === 'before' ? t.sortOrder - 0.5 : t.sortOrder + 0.5;
    d.updatedAt = now;
  }

  return { elements: normalizeSiblings(next), createdGroupId };
}

/** Dissolve a group: its children become top-level (or its parent's children), group removed. */
export function ungroup(elements: Element[], groupId: string): Element[] {
  const group = elements.find((e) => e.id === groupId);
  if (!group) return elements;
  const next = elements
    .filter((e) => e.id !== groupId)
    .map((e) =>
      e.parentId === groupId
        ? { ...e, parentId: group.parentId, sortOrder: group.sortOrder + e.sortOrder / 1000 }
        : { ...e }
    );
  return normalizeSiblings(next);
}
